import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Alert } from "reactstrap";
import style from "./Modal.module.scss";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import FormattedSQL from "./formatter";
import List from "./Parameters/List";
import { QueryStatus } from "./types";

const ModalDynamicReport = ({ report, open, onClose, services }) => {
  const handleClose = () => {
    onClose();
  };
  const [queryStatus, setQueryStatus] = useState(QueryStatus.WaitingExec);
  const [reportResponse, setReportResponse] = useState(undefined);

  const [paramValues, setParamValues] = useState(getInitParams(report));
  const [paramErrors, setParamErrors] = useState({});
  const [alert, setAlert] = useState(false);

  const reportName = report.DynamicReportName;
  const reportQuery = report.DynamicReportSQL;
  const reportId = report.DynamicReportId;

  const handleExecuteButton = async (reportId) => {
    let response = undefined;
    let body = {
      id: reportId,
      parameters: JSON.stringify(paramValues),
    };

    const [errors, hasErrors] = validateParameters(report.Parameters, paramValues);

    setParamErrors(errors);
    setAlert(false);

    if (!hasErrors) {
      setQueryStatus(QueryStatus.Executing);
      await services
        .execute(body)
        .then((res) => res.response)
        .then(({ result, buffer, count }) => {
          if (result.Code == 200) {
            if (count > 0) {
              response = buffer.data;
              setQueryStatus(QueryStatus.Executed);
            } else {
              setQueryStatus(QueryStatus.ExecutedEmpty);
            }
            setAlert({
              color: "buy",
              text: `Succesfully executed. Total rows=${count}`,
            });
          } else {
            setQueryStatus(QueryStatus.Error);
            setAlert({ color: "danger", text: result.Description });
          }
        })
        .catch((e) => {
          setQueryStatus(QueryStatus.WaitingExec);
          setAlert({ color: "danger", text: e.message });
        });
      setReportResponse(response);
    }
  };

  const handleOnChangeParamValue = (name, value) => {
    setParamValues({ ...paramValues, [name]: value });
    setParamErrors({ ...paramErrors, [name]: undefined });
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([new Uint8Array(reportResponse)], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = reportName.trim() + ".xlsx";
      link.click();
      setQueryStatus(QueryStatus.Executed);
    } catch (ex) {
      setQueryStatus(QueryStatus.WaitingExec);
      setAlert({ color: "danger", text: ex.message });
    }
  };

  return (
    <Modal centered isOpen={open} className={style.modal}>
      <ModalHeader toggle={onClose} className={style.header} style={{ padding: "10px 15px" }}>
        {reportName}
      </ModalHeader>
      <ModalBody className={style.body}>
        <FormGroup>
          <Label for="select-report">SQL query</Label>

          <pre className={style.codeStyle}>
            <FormattedSQL query={reportQuery} />
          </pre>
        </FormGroup>

        <List
          parameters={report.Parameters}
          values={paramValues}
          errors={paramErrors}
          onChange={handleOnChangeParamValue}
        />

        {alert && (
          <Alert color={alert.color} className="p-2 mt-2">
            {alert.text}
          </Alert>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="light" className="mr-2" onClick={handleClose}>
          Close
        </Button>

        <LaddaButton
          className="btn btn-ladda btn-primary"
          onClick={() => handleExecuteButton(reportId)}
          data-style={EXPAND_RIGHT}
          loading={queryStatus === QueryStatus.Executing}
        >
          Execute
        </LaddaButton>
        {queryStatus === QueryStatus.Executed && (
          <LaddaButton
            className="btn btn-ladda btn-primary"
            data-style={EXPAND_RIGHT}
            onClick={handleDownload}
          >
            <i className="fa fa-download mr-2"></i>
            <span>Download</span>
          </LaddaButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

function validateParameters(paramDefs, paramValues) {
  let nextErrors = {};
  let hasErrors = false;
  paramDefs.forEach((paramDef) => {
    if (paramDef.ParameterIsMandatory) {
      const value = paramValues[paramDef.ParameterName];
      if (value == undefined || value == "") {
        nextErrors[paramDef.ParameterName] = "Required field";
        hasErrors = true;
      }
    }
  });
  return [nextErrors, hasErrors];
}

function getInitParams(report) {
  const params = {};
  if (report.Parameters) {
    report.Parameters.forEach((param) => {
      const initValue = param.ParameterInitValue;
      if (initValue != "") {
        params[param.ParameterName] = initValue;
      }
    });
  }
  return params;
}

export default ModalDynamicReport;
